import apiV2 from '../api/v2';
import { toDmError } from './dmErrors';

export interface StartMonitoringParams {
    quickstartId: string;
}

export interface SetIntervalParams {
    intervalDays: number;
    /** Follow-up scan delay after a NO-GO; required by DM for Dynamic Aligner
     *  Change monitorings, must be lower than intervalDays. Omit otherwise. */
    requestControlScanAfterDays?: number;
}

export interface PauseMonitoringParams {
    /** Unix timestamp in seconds — when monitoring should auto-resume. */
    resumeDate: number;
    /** Optional message to the patient. */
    comment?: string;
}

export interface MonitoringActionResult {
    success: boolean;
}

export async function startMonitoring(
    patientId: string,
    params: StartMonitoringParams
): Promise<MonitoringActionResult> {
    try {
        const response = await apiV2.post<{ success: boolean; data: MonitoringActionResult }>(
            `/patients/${patientId}/dm/monitoring/start`,
            params
        );
        return response.data.data;
    } catch (err) {
        throw toDmError(err);
    }
}

export async function pauseMonitoring(
    patientId: string,
    params: PauseMonitoringParams
): Promise<MonitoringActionResult> {
    try {
        const response = await apiV2.post<{ success: boolean; data: MonitoringActionResult }>(
            `/patients/${patientId}/dm/monitoring/pause`,
            params
        );
        return response.data.data;
    } catch (err) {
        throw toDmError(err);
    }
}

/** DM marks the patient active either way; a future `resumeDate` (unix seconds) sets the day it counts from. */
export async function resumeMonitoring(
    patientId: string,
    resumeDate?: number
): Promise<MonitoringActionResult> {
    try {
        const response = await apiV2.post<{ success: boolean; data: MonitoringActionResult }>(
            `/patients/${patientId}/dm/monitoring/resume`,
            resumeDate === undefined ? {} : { resumeDate }
        );
        return response.data.data;
    } catch (err) {
        throw toDmError(err);
    }
}

export async function stopMonitoring(patientId: string): Promise<MonitoringActionResult> {
    try {
        const response = await apiV2.post<{ success: boolean; data: MonitoringActionResult }>(
            `/patients/${patientId}/dm/monitoring/stop`
        );
        return response.data.data;
    } catch (err) {
        throw toDmError(err);
    }
}

export async function setMonitoringInterval(
    patientId: string,
    params: SetIntervalParams
): Promise<MonitoringActionResult> {
    try {
        const response = await apiV2.patch<{ success: boolean; data: MonitoringActionResult }>(
            `/patients/${patientId}/dm/monitoring/interval`,
            params
        );
        return response.data.data;
    } catch (err) {
        throw toDmError(err);
    }
}
